// Global
import React from 'react';
import { useRouter } from 'next/router';

// Local
import styles from './SearchResults.module.scss';
import clsx from 'clsx';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';
import { AdvancedContentGrid } from 'components/authorable/shared/card-listing/AdvancedContentGrid/AdvancedContentGrid';
import useDictionary from 'lib/hooks/useDictionary';
import { DEFAULT_CONTENT_PAGE_SIZE } from 'components/authorable/shared/card-listing/CardListingShared';
import { useContentQuery } from 'components/authorable/shared/card-listing/ContentGrid/ContentQueryContext';
import { AcgStaticConfig } from './SearchResults.sitecore';
import {
  consumeRememberedSearchRouteAnnouncement,
  hasPendingSearchRouteAnnouncement,
} from 'lib/a11y/searchRouteAnnouncement';
import { useSearchResultsFallbackContent } from './SearchResultsFallbackContentContext';

export interface SearchResultsProps {
  className?: string;
  theme?: Themes;
  acgConfig?: AcgStaticConfig;
}

const SearchResultsEmptyState = ({
  searchHeading,
  searchTerm,
  noResultsText,
}: {
  searchHeading: string;
  searchTerm?: string;
  noResultsText: string;
}) => {
  const { searchResponse, isLoading } = useContentQuery();
  const trimmedSearchTerm = searchTerm?.trim();
  const resultCount = searchResponse?.Results?.length ?? 0;
  const hasNoResults = !!searchResponse && (searchResponse.Count === 0 || resultCount === 0);

  if (isLoading || !trimmedSearchTerm || !hasNoResults) {
    return null;
  }

  return (
    <div className={styles.noSearchTerm} data-component="search-results-empty-state">
      <h1 className={styles.noSearchTermHeading}>{searchHeading}</h1>
      <p className={styles.noSearchTermText}>{`${noResultsText} "${trimmedSearchTerm}".`}</p>
    </div>
  );
};

const SearchResultsAnnouncement = ({
  searchTerm,
  showingResultText,
  noResultsText,
}: {
  searchTerm?: string;
  showingResultText: string;
  noResultsText: string;
}) => {
  const { searchResponse, isLoading } = useContentQuery();
  const lastAnnouncementRef = React.useRef('');

  const trimmedSearchTerm = searchTerm?.trim();
  const resultCount = searchResponse?.Results?.length ?? 0;
  const hasNoResults = !!searchResponse && (searchResponse.Count === 0 || resultCount === 0);
  const nextAnnouncement =
    !trimmedSearchTerm || isLoading
      ? ''
      : hasNoResults
        ? `${noResultsText} "${trimmedSearchTerm}".`
        : `${showingResultText} "${trimmedSearchTerm}"`;

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Cross-page navigation: _app's route announcer owns the title-then-summary ordering.
    // Suppress our own announcement so we don't speak the summary before the title.
    if (!hasNoResults && hasPendingSearchRouteAnnouncement(nextAnnouncement)) {
      lastAnnouncementRef.current = nextAnnouncement;
      return;
    }

    if (!hasNoResults && consumeRememberedSearchRouteAnnouncement(nextAnnouncement)) {
      lastAnnouncementRef.current = nextAnnouncement;
      return;
    }

    let fallbackAnnouncer: HTMLParagraphElement | null = null;
    const announcer =
      document.getElementById('sr-announcer') ??
      (() => {
        fallbackAnnouncer = document.createElement('p');
        fallbackAnnouncer.setAttribute('aria-live', 'assertive');
        fallbackAnnouncer.setAttribute('aria-atomic', 'true');
        fallbackAnnouncer.setAttribute('role', 'status');
        fallbackAnnouncer.className = 'screenReaderText';
        fallbackAnnouncer.dataset.component = 'search-results-announcement';
        document.body.appendChild(fallbackAnnouncer);
        return fallbackAnnouncer;
      })();

    if (!announcer || !nextAnnouncement || nextAnnouncement === lastAnnouncementRef.current) {
      return () => {
        if (fallbackAnnouncer && document.body.contains(fallbackAnnouncer)) {
          document.body.removeChild(fallbackAnnouncer);
        }
      };
    }

    lastAnnouncementRef.current = nextAnnouncement;
    announcer.textContent = '';

    const frameId = window.requestAnimationFrame(() => {
      announcer.textContent = nextAnnouncement;
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (fallbackAnnouncer && document.body.contains(fallbackAnnouncer)) {
        document.body.removeChild(fallbackAnnouncer);
      }
    };
  }, [nextAnnouncement, hasNoResults]);

  return null;
};

const SearchResultsFallbackContentStateSync = ({ searchTerm }: { searchTerm?: string }) => {
  const { query, searchResponse, isLoading } = useContentQuery();
  const { setFallbackContentState } = useSearchResultsFallbackContent();
  const trimmedSearchTerm = searchTerm?.trim();
  const resultCount = searchResponse?.Results?.length ?? 0;
  const searchResponseMatchesCurrentTerm = query?.searchTerm === trimmedSearchTerm;
  const showFallbackContent =
    !!trimmedSearchTerm &&
    !!searchResponse &&
    !isLoading &&
    searchResponseMatchesCurrentTerm &&
    (searchResponse.Count === 0 || resultCount === 0);

  React.useEffect(() => {
    if (!trimmedSearchTerm) {
      setFallbackContentState('notSearchResults');
      return;
    }

    setFallbackContentState(showFallbackContent ? 'visible' : 'hidden');

    return () => setFallbackContentState('notSearchResults');
  }, [setFallbackContentState, showFallbackContent, trimmedSearchTerm]);

  return null;
};

const SearchResultsGrid = ({
  acgConfig,
  searchTerm,
  searchType,
  headline,
  headlineSubhead,
  showingResultText,
  noResultsText,
}: {
  acgConfig?: AcgStaticConfig;
  searchTerm?: string;
  searchType?: 'Standard' | 'TypeAhead' | 'PreSearch';
  headline?: string;
  headlineSubhead?: React.ReactNode;
  showingResultText: string;
  noResultsText: string;
}) => {
  const hasRuntimeSearchTerm = !!searchTerm?.trim();

  return (
    <AdvancedContentGrid
      searchTerm={searchTerm}
      searchType={searchType}
      searchAnalyticsEnabled={hasRuntimeSearchTerm}
      initialSearchResponse={hasRuntimeSearchTerm ? null : (acgConfig?.staticProps?.result ?? null)}
      contentListItem={acgConfig?.contentListItem ?? undefined}
      facetConfig={acgConfig?.searchFacetConfig ?? undefined}
      contentOverrides={acgConfig?.staticProps?.contentOverrides ?? {}}
      fieldsToShow={acgConfig?.fieldsToShow}
      columns={acgConfig?.columns}
      gridAspectRatio={acgConfig?.gridAspectRatio}
      preambleOrientation={acgConfig?.preambleOrientation}
      cardAlignment={acgConfig?.cardAlignment}
      showFacets={acgConfig?.showFacets ?? false}
      showPagination={acgConfig?.showPagination}
      showResultCount={acgConfig?.showResultCount}
      showSorting={acgConfig?.showSorting}
      centerLastRow={acgConfig?.centerLastRow}
      constrainedComponentWidth={acgConfig?.constrainedComponentWidth}
      pageSize={acgConfig?.maxCards ?? DEFAULT_CONTENT_PAGE_SIZE}
      sortByOptions={acgConfig?.sortByOptions ?? undefined}
      sortByDirections={acgConfig?.sortByDirections ?? undefined}
      headline={headline}
      headlineSubhead={headlineSubhead}
    >
      <SearchResultsFallbackContentStateSync searchTerm={searchTerm} />
      <SearchResultsAnnouncement
        searchTerm={searchTerm}
        showingResultText={showingResultText}
        noResultsText={noResultsText}
      />
      <SearchResultsEmptyState
        searchHeading={headline ?? 'Search'}
        searchTerm={searchTerm}
        noResultsText={noResultsText}
      />
    </AdvancedContentGrid>
  );
};

export const SearchResults = (props: SearchResultsProps) => {
  const { className, theme, acgConfig } = props;
  const router = useRouter();
  const { getDictionaryValue } = useDictionary();

  const searchTerm =
    router.isReady && typeof router.query.term === 'string' ? router.query.term : '';
  const searchType =
    router.isReady && typeof router.query.searchType === 'string'
      ? (router.query.searchType as 'Standard' | 'TypeAhead' | 'PreSearch')
      : 'Standard';

  const searchHeading = getDictionaryValue('Components.Search.Heading') || 'Search';
  const defaultText = getDictionaryValue('Components.Search.DefaultText');
  const showingResultText =
    getDictionaryValue('Components.Search.ShowingResult') || 'Showing results for';
  const noResultsText =
    getDictionaryValue('Components.Search.NoResultsText') ||
    "Sorry, we couldn't find any results for";

  return (
    <BrandAndThemeProvider theme={theme}>
      <section className={clsx(styles.searchResults, className)} aria-label="Search results">
        {searchTerm ? (
          <SearchResultsGrid
            acgConfig={acgConfig}
            searchTerm={searchTerm}
            searchType={searchType}
            headline={searchHeading}
            showingResultText={showingResultText}
            noResultsText={noResultsText}
            headlineSubhead={
              <>
                {showingResultText} &ldquo;<strong>{searchTerm}</strong>&rdquo;
              </>
            }
          />
        ) : (
          <div className={styles.noSearchTerm}>
            <h1 className={styles.noSearchTermHeading}>{searchHeading}</h1>
            {defaultText && <p className={styles.noSearchTermText}>{defaultText}</p>}
          </div>
        )}
      </section>
    </BrandAndThemeProvider>
  );
};
